/* =============================================================================
   Drag-to-scroll for horizontal scrollers

   Opt in from markup with `data-drag-scroll` on the scrolling element. Every
   matching element is wired on load and again on `shopify:section:load`, so
   sections re-rendered by the theme editor keep the behaviour.

   The gesture is the mouse/pen affordance only — touch pointers are left alone
   so native momentum scrolling is untouched, and the scrollbar is hidden by
   design on these tracks.

   Built for tracks whose children are links (product cards), which drives two
   choices. Pointer capture is deliberately NOT used: an active capture
   retargets the following click and would break an ordinary card click. And the
   click that closes a real drag is suppressed, otherwise letting go of the
   mouse opens whichever card sits under the cursor.
   ============================================================================= */

(() => {
  'use strict';

  /* Pointer travel before a press counts as a drag rather than a click. */
  const DRAG_THRESHOLD = 4;

  const wired = new WeakSet();

  const enable = (track) => {
    if (wired.has(track)) return;
    wired.add(track);

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let isDragging = false;
    let suppressClick = false;

    const onPointerMove = (event) => {
      if (event.pointerId !== pointerId) return;

      const walk = event.clientX - startX;

      if (!isDragging) {
        if (Math.abs(walk) < DRAG_THRESHOLD) return;

        isDragging = true;
        suppressClick = true;
        track.classList.add('is-dragging');
        document.getSelection()?.removeAllRanges();
      }

      event.preventDefault();
      track.scrollLeft = startScrollLeft - walk;
    };

    const endDrag = (event) => {
      if (event.pointerId !== pointerId) return;

      pointerId = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);

      /* Dropping the class restores mandatory snap, which lands the row on the
         nearest card. */
      isDragging = false;
      track.classList.remove('is-dragging');
    };

    const onPointerDown = (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      /* Nothing to drag, and a press should stay a plain click. */
      if (track.scrollWidth - track.clientWidth <= 1) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
      isDragging = false;
      /* Cleared on every press, so a drag that ended outside the track cannot
         leave the flag armed and swallow a later legitimate click. */
      suppressClick = false;

      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    };

    track.addEventListener('pointerdown', onPointerDown);

    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;

        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    /* Cards are images and links, so the browser's native drag ghost would
       otherwise hijack the gesture as soon as the pointer moves. */
    track.addEventListener('dragstart', (event) => {
      if (pointerId !== null) event.preventDefault();
    });

    /* Marks the track only while it actually overflows, so the grab cursor never
       promises a gesture that cannot move anything. Content arrives from a fetch
       and the overflow also comes and goes with the viewport, hence both
       observers. */
    const syncAffordance = () => {
      track.classList.toggle('is-draggable', track.scrollWidth - track.clientWidth > 1);
    };

    syncAffordance();
    new ResizeObserver(syncAffordance).observe(track);
    new MutationObserver(syncAffordance).observe(track, { childList: true });
  };

  const enableWithin = (root) => {
    if (!root?.querySelectorAll) return;

    if (root.matches?.('[data-drag-scroll]')) enable(root);
    root.querySelectorAll('[data-drag-scroll]').forEach(enable);
  };

  const initAll = () => enableWithin(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => enableWithin(event.target));
})();
